/* eslint-disable @typescript-eslint/no-unsafe-call */
import {
  Button,
  Tooltip,
  PopoverMenu,
  Icon,
  MiniLoader,
} from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import featureFlags from 'common/helpers/featureFlags';
import classNames from 'classnames';
import useDeviceViewport from 'common/hooks/useDeviceViewport';
import { deviceViewportNames } from 'common/constants/DeviceViewports';
import { TFunction } from 'i18next';
import ModalConfirmation from '../ModalConfirmation/ModalConfirmation';
import styles from './FormActions.module.scss';
import Separator from '../Separator';
import useModal from './useModal';

type ContentType = 'kfa' | 'analyses';
type Position = 'bottom' | 'top';

const createDeleteModalBody = (project: string, item: string, t: TFunction) => {
  return t('deleteConfirmationBody', {
    name: item,
    projectName: project,
  });
};

interface Props {
  isDeleteDisabled?: boolean;
  projectName?: string;
  itemTitle?: string;
  deleteAction?: (callback?: () => void) => void;
  cancelAction: () => void;
  reportAction: () => void;
  doneAction: () => void;
  isReportDisabled: boolean;
  isDoneDisabled: boolean;
  position: Position;
  isSaving: boolean;
  isSavingFulfilled: boolean;
  isSavingRejected: boolean;
  isCreateMode: boolean;
  contentType: ContentType;
  isPreviewHidden: boolean;
}

const KFA_REPORT_TOOLTIP =
  'This shows a preview of the Key Focus Area in the report. New content will be saved and added to Junction so that a preview can be generated.';

const A_REPORT_TOOLTIP =
  'This shows a preview of the analysis in the report. New content will be saved and added to Junction so that a preview can be generated.';

const FormActions = ({
  projectName,
  itemTitle,
  deleteAction,
  cancelAction,
  reportAction,
  doneAction,
  isReportDisabled,
  isDoneDisabled,
  isDeleteDisabled,
  position,
  isSaving,
  isSavingFulfilled,
  isSavingRejected,
  isCreateMode,
  contentType,
  isPreviewHidden,
}: Props) => {
  const { t } = useTranslation();
  const currentDeviceViewport = useDeviceViewport();
  const isTablet =
    currentDeviceViewport === deviceViewportNames.tablet ||
    currentDeviceViewport === deviceViewportNames.tabletL;

  let previewLabel = isCreateMode ? 'Save and preview' : 'Preview report';
  if (!featureFlags.isAutoSaveEnabled)
    previewLabel = isCreateMode ? 'Save and preview' : 'Update and Preview';

  const publishLabel = isCreateMode ? 'Add' : 'Update';

  const { isShowing, toggle } = useModal();

  const popoverMenu = (label: string) => (
    <PopoverMenu
      placement="bottom-end"
      id="popover-moremenu"
      data-testid="save-and-preview-popover"
      className="mr-3"
      isButtonSecondary
    >
      <Button
        className="button-menu"
        onClick={reportAction}
        data-testid="save-and-preview-popover-button"
        disabled={isReportDisabled}
        aria-label={label}
        icon={undefined}
        iconClasses={undefined}
      >
        <Icon name="report" width="21" className="outline icon-orange" />
        {label}
      </Button>
    </PopoverMenu>
  );

  const previewReportButton = (
    label: string,
    bPosition: Position,
    contentT: ContentType
  ) => (
    <Tooltip
      direction={bPosition === 'bottom' ? 'top' : 'bottom'}
      tip={contentT === 'kfa' ? KFA_REPORT_TOOLTIP : A_REPORT_TOOLTIP}
      dataTestId={undefined}
      unthemed={undefined}
      showingInitial={undefined}
    >
      <Button
        onClick={reportAction}
        data-testid="preview-button"
        type="button"
        disabled={isReportDisabled}
        aria-label={label}
        icon={undefined}
        iconClasses={undefined}
        className={classNames(
          'button-xs button-outline mx-2',
          styles['preview-button']
        )}
      >
        {label}
      </Button>
    </Tooltip>
  );

  const publishButton = (label: string, bPosition: Position) => (
    <Button
      className={
        bPosition === 'bottom'
          ? 'mr-3 button-block button-md'
          : 'mr-3 ml-2 button-xs'
      }
      type="button"
      disabled={isDoneDisabled}
      aria-label={label}
      onMouseDown={doneAction}
      icon={undefined}
      iconClasses={undefined}
      data-testid={
        bPosition === 'bottom' ? 'publish-button-bottom' : 'publish-button-top'
      }
    >
      {label}
    </Button>
  );

  const cancelButton = (bPosition: Position) => (
    <Button
      className={
        bPosition === 'bottom'
          ? 'button-md button-link button-theme mx-2 mr-2'
          : 'button-link button-theme mx-2 mr-2'
      }
      type="button"
      onClick={cancelAction}
      data-testid={
        bPosition === 'bottom' ? 'cancel-button-bottom' : 'cancel-button-top'
      }
      icon={undefined}
      iconClasses={undefined}
      aria-label={t('cancel')}
    >
      {t('cancel')}
    </Button>
  );

  const savingIndicator = (
    <div className={styles.saving}>
      <MiniLoader monochromatic />
      <div className={styles.message}>{t('saving')}</div>
    </div>
  );

  const savedIndicator = (
    <div className={styles.saving}>
      <Icon name="confirmed" className="outline icon-green" />
      <div className={styles.message}>{t('saved')}</div>
    </div>
  );

  const savedRejectedIndicator = (
    <div className={styles.saving}>
      <Icon name="alert" className="fill icon-pink saving-error" />
      <div className={styles.message}>{t('savingFailed')}</div>
    </div>
  );

  const putSeparator = (atPosition: string) =>
    atPosition === 'bottom' ? (
      <div className="analysis-form-actions-separator mt-3">
        <Separator />
      </div>
    ) : null;

  const isBottom = (placement: string) => placement !== 'top';
  if (featureFlags.isAutoSaveEnabled && position === 'top') {
    return (
      <>
        <ModalConfirmation
          show={isShowing}
          onCancel={toggle}
          onConfirm={() => {
            if (deleteAction) {
              deleteAction(toggle);
            }
          }}
          confirmTitle={t('areyouSureDeleteMsg')}
          confirmationMessage={createDeleteModalBody(
            projectName as string,
            itemTitle as string,
            t
          )}
          confirmButtonText={t('delete')}
        />
        <div className={styles.container}>
          {isSaving && savingIndicator}
          {!isSaving && isSavingFulfilled && savedIndicator}
          {!isSaving && isSavingRejected && savedRejectedIndicator}
          <Button
            disabled={isDeleteDisabled}
            className="button-md button-link button-theme mr-2"
            type="button"
            onClick={toggle}
            icon={undefined}
            iconClasses={undefined}
            aria-label={t('delete')}
          >
            {t('deleteKeyFocusArea')}
          </Button>
          {!isPreviewHidden &&
            previewReportButton(previewLabel, position, contentType)}
          {publishButton(t('doneAnalysis'), position)}
        </div>
      </>
    );
  }

  if (!featureFlags.isAutoSaveEnabled && isTablet) {
    return (
      <>
        {putSeparator(position)}
        <div
          className={classNames({
            'form-actions': true,
            bottom: isBottom(position),
          })}
        >
          {cancelButton(position)}
          {!isPreviewHidden && publishButton(publishLabel, position)}
          {popoverMenu(previewLabel)}
        </div>
      </>
    );
  }

  if (!featureFlags.isAutoSaveEnabled && !isTablet) {
    return (
      <>
        {putSeparator(position)}
        <div
          className={classNames({
            'form-actions': true,
            bottom: isBottom(position),
          })}
        >
          {cancelButton(position)}
          {!isPreviewHidden &&
            previewReportButton(previewLabel, position, contentType)}
          {publishButton(publishLabel, position)}
        </div>
      </>
    );
  }

  return <></>;
};

export default FormActions;
